import React from 'react';
import { useTranslation } from 'react-i18next';
import { Droppable, Draggable } from '@hello-pangea/dnd';
import { railTeamDragId } from './heatDragIds';
import { dropStyle } from './dnd';
import { GripVertical } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { cn } from '../../lib/utils';
import { estInscriptionAnnulee } from '../../lib/registrationStatus';
import type { Heat, HeatParticipant, Team, WOD } from '../../types/heats';

interface Props {
  teams: Team[];
  heats: Heat[];
  /** Les WODs de l'événement : un WOD à prérequis (LOG-219) ne prend que ses qualifiées. */
  wods: WOD[];
  heatParticipants: Record<string, HeatParticipant[]>;
  /** `'all'` ou l'id d'un WOD : définit ce que « à placer » veut dire. */
  wodFilter: string;
  onWodFilterChange: (v: string) => void;
  categoryFilter: string;
  onCategoryFilterChange: (v: string) => void;
  search: string;
  onSearchChange: (v: string) => void;
}

/**
 * Équipes restant à placer, glissables vers un heat.
 *
 * ⚠️ « Non assignée » est relatif à un WOD, pas absolu : une équipe placée sur
 * le WOD 1 reste à placer pour le WOD 2. D'où le sélecteur de WOD — sans lui,
 * dès qu'un événement a plusieurs WODs, la liste ne veut plus rien dire.
 * Avec « tous », on ne masque une équipe que lorsqu'elle est placée sur
 * **chacun** des WODs de l'événement.
 */
const UnassignedTeamsRail: React.FC<Props> = ({
  teams, heats, wods, heatParticipants,
  wodFilter, onWodFilterChange,
  categoryFilter, onCategoryFilterChange,
  search, onSearchChange,
}) => {
  const { t } = useTranslation();
  const noCategory = t('assignments.noCategory');

  const wodIds = Array.from(new Set(heats.map(h => h.wod_id).filter(Boolean))) as string[];
  const hasWods = wodIds.length > 0;

  // WOD éliminatoire (LOG-219) : une équipe n'est « à placer » sur un WOD à
  // prérequis que si elle a TERMINÉ ce prérequis. Le serveur le recalcule à
  // chaque lecture (`qualified_team_ids`) — le rail ne fait que le lire.
  const qualifiedFor = (wodId: string, teamId: string): boolean => {
    const wod = wods.find(w => w.id === wodId);
    if (!wod || !wod.requires_finished_wod_id) return true;
    return (wod.qualified_team_ids || []).includes(teamId);
  };

  const matchesFilters = (team: Team) => {
    // Une inscription annulée sort l'équipe de la compétition : l'annulation
    // l'a retirée de ses heats, la laisser « à placer » reviendrait à défaire
    // ce retrait au geste suivant. Le serveur applique la même règle à la
    // génération, à l'auto-assignation et au dépôt (409).
    if (estInscriptionAnnulee(team.registration)) return false;
    if (categoryFilter !== 'all' && (team.category || noCategory) !== categoryFilter) return false;
    if (search && !team.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  };

  const unassigned = React.useMemo(() => {
    if (wodFilter === 'all') {
      return teams.filter(team => {
        // Une équipe disparaît du rail seulement si elle est placée sur TOUS
        // les WODs : sinon il lui reste au moins une épreuve à assigner.
        const assignedWodIds = new Set<string>();
        heats.forEach(heat => {
          if ((heatParticipants[heat.id] || []).some(p => p.team_id === team.id)) {
            if (heat.wod_id) assignedWodIds.add(heat.wod_id);
          }
        });
        // Un WOD dont l'équipe n'est pas qualifiée ne lui reste pas « à placer ».
        const wodsToPlace = wodIds.filter(id => qualifiedFor(id, team.id));
        if (hasWods && wodsToPlace.every(id => assignedWodIds.has(id))) return false;
        // Événement sans WOD (course) : l'appartenance est simplement globale.
        if (!hasWods && Object.values(heatParticipants).some(ps => ps.some(p => p.team_id === team.id))) return false;
        return matchesFilters(team);
      });
    }

    const assignedForWod = new Set<string>();
    heats.filter(h => h.wod_id === wodFilter).forEach(heat => {
      (heatParticipants[heat.id] || []).forEach(p => assignedForWod.add(p.team_id));
    });
    return teams.filter(team => !assignedForWod.has(team.id) && qualifiedFor(wodFilter, team.id) && matchesFilters(team));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teams, heats, wods, heatParticipants, wodFilter, categoryFilter, search, noCategory]);

  const grouped = unassigned.reduce<Record<string, Team[]>>((acc, team) => {
    const key = team.category || noCategory;
    (acc[key] ||= []).push(team);
    return acc;
  }, {});

  const categories = Array.from(new Set(teams.map(x => x.category || noCategory)));

  return (
    <Card data-tour="assign-teams" className="md:flex md:min-h-0 md:flex-1 md:flex-col">
      <CardContent className="p-4 md:flex md:min-h-0 md:flex-1 md:flex-col">
        <p className="text-sm text-muted-foreground mb-3">
          {t('assignments.availableTeams', { count: unassigned.length })}
        </p>

        <div className="space-y-2 mb-3">
          {hasWods && (
            <Select value={wodFilter} onValueChange={onWodFilterChange}>
              <SelectTrigger><SelectValue placeholder="WOD" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('assignments.filters.all')}</SelectItem>
                {wodIds.map(wodId => {
                  const heat = heats.find(h => h.wod_id === wodId);
                  return (
                    <SelectItem key={wodId} value={wodId}>
                      {heat?.wod_name || `WOD ${wodId}`}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          )}

          {wodFilter !== 'all' && wods.find(w => w.id === wodFilter)?.requires_finished_wod_id && (
            <p className="text-xs text-warning">
              {t('assignments.prerequisiteOnly', { name: wods.find(w => w.id === wodFilter)?.requires_finished_wod_name || '' })}
            </p>
          )}

          <Select value={categoryFilter} onValueChange={onCategoryFilterChange}>
            <SelectTrigger><SelectValue placeholder={t('assignments.filters.category')} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('assignments.filters.allCategories')}</SelectItem>
              {categories.map(cat => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Input
            placeholder={t('assignments.filters.search')}
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>

        {/* Déposer une équipe ici la retire de son heat. */}
        <Droppable droppableId="unassigned" type="TEAM">
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className={cn(
                "min-h-[300px] overflow-y-auto rounded p-1 md:min-h-0 md:flex-1",
                snapshot.isDraggingOver && "bg-white/[0.06]"
              )}
            >
              {Object.entries(grouped).map(([category, list]) => (
                <div key={category} className="mb-3">
                  <p className="mb-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{category}</p>
                  {list.map((team, index) => (
                    <Draggable key={team.id} draggableId={railTeamDragId(team.id)} index={index}>
                      {(dragProvided, dragSnapshot) => (
                        <div
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          {...dragProvided.dragHandleProps}
                          style={dropStyle(dragProvided.draggableProps.style, dragSnapshot)}
                          className={cn(
                            // Une carte dans une carte : le rail en avait une, et
                            // chaque équipe aussi. Les équipes deviennent des lignes
                            // ouvertes séparées par un filet — la doctrine « sections
                            // ouvertes » déjà appliquée sur Judge, Athlete et Organizer.
                            // La vignette de glissement, elle, redevient un objet : c'est
                            // le seul moment où l'équipe flotte au-dessus du reste.
                            "p-3 md:p-2 flex items-center gap-2 cursor-grab border-b border-border/70",
                            dragSnapshot.isDragging
                              ? "rounded border border-primary bg-card rim shadow-lg scale-[1.02]"
                              : "hover:bg-white/[0.04]"
                          )}
                        >
                          <GripVertical className="h-4 w-4" />
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-sm truncate">{team.name}</p>
                            {team.team_id && (
                              <p className="text-xs text-muted-foreground">#{team.team_id}</p>
                            )}
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                </div>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </CardContent>
    </Card>
  );
};

export default UnassignedTeamsRail;
